import { ChangeDetectionStrategy, Component, computed, effect, inject, model, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePicker } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { MessageService } from 'primeng/api';
import { DeviceContextService } from '../device-context.service';
import { CloudApiService } from '../../../services/cloud-api.service';
import { deviceDisplayName } from '@/cloud/services/device-display';

interface VersionOption {
    label: string;
    value: string; // composite: packageId:versionId
    packageId: string;
    packageName: string;
    versionId: string;
    assemblyVersion: string;
    isLatest: boolean;
    isDebugBuild: boolean;
    releaseNotes?: string;
    uploadedUtc?: string;
    uploadedBy?: string;
}

// Matches timezone-qualified timestamps (UTC suffix or explicit ±HH:mm offset).
const TIMEZONE_SUFFIX_REGEX = /[Zz]|[+-]\d{2}:\d{2}$/;

@Component({
    selector: 'app-deploy-version-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, DatePicker, DialogModule, SelectModule, TagModule],
    templateUrl: './deploy-version-dialog.html',
    styleUrl: './deploy-version-dialog.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeployVersionDialog {
    private ctx = inject(DeviceContextService);
    private api = inject(CloudApiService);
    private messageService = inject(MessageService);

    readonly visible = model(false);

    readonly versions = signal<VersionOption[]>([]);
    readonly loading = signal(false);
    readonly loadError = signal<string | null>(null);
    readonly selectedKey = signal<string | null>(null);
    readonly scheduleEnabled = signal(false);
    readonly scheduledFor = signal<Date | null>(null);
    readonly submitting = signal(false);
    readonly submitError = signal<string | null>(null);

    readonly minScheduleDate = new Date();

    readonly deviceName = computed(() => deviceDisplayName(this.ctx.device()));

    // Package namespace the device reported at check-in; only packages built for it can be deployed.
    readonly devicePackageNamespace = computed(() => {
        const device = this.ctx.device();
        const reported = device?.packageNamespace ?? device?.payload?.appNamespace;
        return typeof reported === 'string' && reported.trim() ? reported : null;
    });

    readonly currentVersion = computed(() => {
        const device = this.ctx.device();
        const version = device?.payload?.appAssemblyVersion ?? device?.version;
        return typeof version === 'string' && version.trim() ? version : null;
    });

    readonly selectedVersion = computed(() => {
        const key = this.selectedKey();
        if (!key) return null;
        return this.versions().find((v) => v.value === key) ?? null;
    });

    // Upload stamp of the picked version. The API returns some timestamps without a
    // timezone suffix, which the browser would otherwise read as local time.
    readonly selectedUploadedLabel = computed(() => {
        const uploaded = this.selectedVersion()?.uploadedUtc;
        if (!uploaded) return null;
        const parsed = new Date(TIMEZONE_SUFFIX_REGEX.test(uploaded) ? uploaded : `${uploaded}Z`);
        return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString();
    });

    readonly latestVersion = computed(() => this.versions().find((v) => v.isLatest) ?? this.versions()[0] ?? null);

    // Relationship between the picked version and what the device is running, so the
    // operator sees a re-deploy or a downgrade before queueing it.
    readonly selectionNotice = computed<{ severity: 'info' | 'warn'; text: string } | null>(() => {
        const selected = this.selectedVersion();
        const current = this.currentVersion();
        if (!selected || !current) return null;
        const cmp = this.compareVersions(selected.assemblyVersion, current);
        if (cmp === 0) return { severity: 'info', text: `This device already reports v${current}. Deploying re-installs the same version.` };
        if (cmp < 0) return { severity: 'warn', text: `This is a downgrade — the device is running v${current}.` };
        return null;
    });

    readonly scheduleError = computed<string | null>(() => {
        if (!this.scheduleEnabled()) return null;
        const when = this.scheduledFor();
        if (!when) return 'Pick a date and time, or switch back to deploying on the next check-in.';
        if (Number.isNaN(when.getTime())) return 'Scheduled date is invalid.';
        return null;
    });

    readonly canSubmit = computed(() => !this.submitting() && !!this.selectedVersion() && !this.scheduleError());

    private fetchToken = 0;

    constructor() {
        // Load the version list each time the dialog opens so newly uploaded packages appear.
        // Device snapshots tick on every check-in, so read them untracked to avoid reloading
        // the list underneath the operator while the dialog is open.
        effect(() => {
            if (!this.visible()) return;
            untracked(() => {
                const namespaceId = this.ctx.device()?.appNamespaceId;
                if (!namespaceId) return;
                this.loadVersions(namespaceId, this.devicePackageNamespace());
            });
        });
    }

    private async loadVersions(namespaceId: string, packageNamespace: string | null): Promise<void> {
        const requestId = ++this.fetchToken;
        this.loading.set(true);
        this.loadError.set(null);

        try {
            const packageList = await this.api.getPackages(namespaceId);
            if (requestId !== this.fetchToken) return;

            // Restrict to packages built for this device's application namespace. Devices that
            // have not reported one yet (never checked in) fall back to every package listed.
            const candidates = packageNamespace ? packageList.filter((pkg) => pkg.programNamespace === packageNamespace) : packageList;

            const options: VersionOption[] = [];
            for (const pkg of candidates) {
                try {
                    // The whole history, so any version can be a rollback target.
                    const versions = await this.api.getAllPackageVersions(pkg.id);
                    if (requestId !== this.fetchToken) return;
                    for (const version of versions) {
                        options.push({
                            label: `${pkg.name} v${version.assemblyVersion}`,
                            value: `${pkg.id}:${version.id}`,
                            packageId: pkg.id,
                            packageName: pkg.name,
                            versionId: version.id,
                            assemblyVersion: version.assemblyVersion,
                            isLatest: version.isLatest,
                            isDebugBuild: version.isDebugBuild ?? false,
                            releaseNotes: version.releaseNotes,
                            uploadedUtc: version.uploadedUtc,
                            uploadedBy: version.uploadedBy
                        });
                    }
                } catch {
                    // Skip packages whose details we cannot read; the rest still deploy.
                }
            }

            options.sort((a, b) => this.compareVersions(b.assemblyVersion, a.assemblyVersion) || a.packageName.localeCompare(b.packageName));

            if (requestId !== this.fetchToken) return;
            this.versions.set(options);

            // Preselect the latest version so the common case is one click.
            const current = this.selectedKey();
            if (!current || !options.some((o) => o.value === current)) {
                this.selectedKey.set(options.find((o) => o.isLatest)?.value ?? options[0]?.value ?? null);
            }
        } catch (err) {
            if (requestId === this.fetchToken) this.loadError.set(this.toErrorMessage(err));
        } finally {
            if (requestId === this.fetchToken) this.loading.set(false);
        }
    }

    selectLatest(): void {
        const latest = this.latestVersion();
        if (latest) this.selectedKey.set(latest.value);
    }

    onScheduleToggle(enabled: boolean): void {
        this.scheduleEnabled.set(enabled);
        if (!enabled) this.scheduledFor.set(null);
    }

    async deploy(): Promise<void> {
        const device = this.ctx.device();
        const selected = this.selectedVersion();
        if (!device?.id || !selected || !this.canSubmit()) return;

        const scheduledFor = this.scheduleEnabled() ? this.scheduledFor() : null;
        this.submitting.set(true);
        this.submitError.set(null);
        try {
            await this.api.deployPackage(selected.packageId, selected.versionId, device.id, scheduledFor ? scheduledFor.toISOString() : undefined);
            this.messageService.add({
                severity: 'success',
                summary: scheduledFor ? 'Update Scheduled' : 'Update Queued',
                detail: scheduledFor
                    ? `${selected.packageName} v${selected.assemblyVersion} scheduled for ${this.deviceName()} at ${scheduledFor.toLocaleString()}`
                    : `${selected.packageName} v${selected.assemblyVersion} will deploy to ${this.deviceName()} on its next check-in`
            });
            this.visible.set(false);
            this.reset();
        } catch (err) {
            this.submitError.set(this.toErrorMessage(err));
        } finally {
            this.submitting.set(false);
        }
    }

    reset(): void {
        this.scheduleEnabled.set(false);
        this.scheduledFor.set(null);
        this.submitError.set(null);
        this.selectedKey.set(null);
    }

    // Numeric segment comparison ("2026.1.2" vs "2026.1.10"); missing segments count as 0.
    private compareVersions(a: string, b: string): number {
        const partsA = (a || '').split('.').map((p) => parseInt(p, 10) || 0);
        const partsB = (b || '').split('.').map((p) => parseInt(p, 10) || 0);
        const length = Math.max(partsA.length, partsB.length);
        for (let i = 0; i < length; i++) {
            const numA = partsA[i] || 0;
            const numB = partsB[i] || 0;
            if (numA !== numB) return numA < numB ? -1 : 1;
        }
        return 0;
    }

    private toErrorMessage(err: any): string {
        if (!err) return 'Unknown error';
        if (typeof err === 'string') return err;
        if (err.error) {
            if (typeof err.error === 'string') return err.error;
            if (err.error?.error) return err.error.error;
            if (err.error?.message) return err.error.message;
        }
        if (err.message) return err.message;
        return 'Request failed';
    }
}
